import { Word } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import type * as ngrxStore from '@ngrx/store'
import * as rxjs from 'rxjs'

import type { Feature } from '../feature'

import type { Op } from './op'
import { Phase } from './phase'

// TODO: clean up comments

/**
 * Action used within the entity collection system. This action extends the base
 * ngrx {@link https://ngrx.io/guide/store/actions action}, allowing it to be
 * used within the ngrx system.
 */
export class Action<
  // Used as input and output for reducer
  in out TState = unknown,
  // All covariant since we want Action with generic op and data to be super
  // type to Action with specific op and data
  out TOp extends Op = Op,
  out TData = unknown,
> implements ngrxStore.Action
{
  /**
   * Used to determine {@link Action.phase}
   */
  private static readonly _requestIdsThatHaveHadResponses = new Set<string>()

  /**
   * @see {@link Op}
   */
  public readonly op: TOp
  /**
   * @see {@link Phase}
   */
  public readonly phase: Phase | undefined
  /**
   * The payload associated with the action. This data is used to convey
   * information to the state, effects, reducers, or other actions. It can be of
   * any type, ranging from primitive values to complex objects or even function
   * callbacks.
   */
  public readonly data: TData

  /** Id of {@link Feature} that created this {@link Action} */
  public readonly featureId: string

  /** Returns {@link Feature} that created this {@link Action} */
  public readonly getFeature: () => Feature.Default

  /**
   * Describes the origin from where the action was dispatched. This helps in
   * identifying what triggered this action, such as a component, service, or
   * effect.
   *
   * Being descriptive with the source is crucial for tracing the origin of the
   * action, which can be useful for debugging and logging purposes.
   *
   * Conventions to follow:
   * - Use `.` to separate different parts of the source.
   * - The last part of the source should describe the event that triggered the
   *   action, in past tense.
   *
   * @example
   * 'Table.Header.Buttons.Print.Clicked'
   * 'MappingSlideout.Footer.Buttons.Save.Clicked'
   */
  public readonly origin: string

  /**
   * Represents the immediate trigger of this {@link Action}
   *
   * This can be another {@link Action}, or a `string` describing the
   * {@link this.sourceOrigin origin} of this {@link Action}
   */
  public readonly triggeredBy?: Action

  /**
   * TODO:
   *
   * @example
   * 'Table Plus Button Clicked • ec/Widgets/custom:WidgetPreviewPlugin » getWidgetPreview/request'
   * 'myComponent.ngOnInit • ec/Widgets/custom:WidgetPreviewPlugin » getWidgetPreview/request'
   */
  public readonly type: string

  /**
   * Additional data for storing extra context related to the action and all of
   * the previous actions in its chain
   */
  public readonly traceStash?: Record<string, unknown>

  /**
   * Uniquely identifies a chain of actions. Every {@link Action} will have the
   * same {@link traceId} as its {@link triggeredBy} {@link Action}
   */
  public readonly traceId: string

  /**
   * Uniquely identifies a specific request
   *
   * Only relevant when {@link Op.status} is defined
   */
  public readonly requestId?: string

  /**
   * Produces a new state which is the result of this {@link Action} applied to
   * the given state
   */
  public readonly reducer: (
    state: TState,
    globalState: object,
  ) => TState | undefined

  constructor({
    data,
    feature,
    op,
    reducerDeprecated,
    reducer,
    source,
    traceStash,
  }: Readonly<Action.constructor.Params<TState, TOp, TData>>) {
    this.op = op
    this.data = data
    this.featureId = feature.id
    this.getFeature = () => feature satisfies Feature.Default as Feature.Default
    this.reducer = reducer
      ? (state, globalState) =>
          reducer({
            action: this,
            data,
            globalState,
            state,
          })
      : reducerDeprecated
        ? (state, globalState) => reducerDeprecated(state, data, globalState)
        : state => state

    if (source instanceof Action) {
      this.origin = source.origin
      this.triggeredBy = source
      this.traceStash = {
        ...source?.traceStash,
        ...traceStash,
      }
      this.traceId = source.traceId
    } else {
      this.origin = source
      this.traceStash = { ...traceStash }
      this.traceId = Word.generate()
    }

    this.type = `${this.origin} • ${feature.id} » ${op.name}${op.status ? `/${op.status}` : ''}`

    if (op.status === 'request') {
      // If this is a request, we generate a new request ID
      this.requestId = Word.generate()
      this.phase = Phase.Request
    } else if (
      this.triggeredBy &&
      this.triggeredBy.op.name === this.op.name &&
      this.triggeredBy.featureId === feature.id
    ) {
      // Otherwise, if this is triggered by a request of the same op name, then
      // we use that request ID, if there is one
      this.requestId = this.triggeredBy.requestId
      if (this.requestId !== undefined) {
        if (Action._requestIdsThatHaveHadResponses.has(this.requestId)) {
          // If the request had a previous response, this is a follow up
          this.phase = Phase.FollowUp
        } else {
          // Otherwise, this is the first response
          Action._requestIdsThatHaveHadResponses.add(this.requestId)
          this.phase = Phase.Response
        }
      }
    } else if (op.status !== undefined) {
      console.warn(
        `Cannot identify the request associated with this action: '${this.type}'. This may cause unexpected behaviors`,
      )
    }
  }

  /**
   * Dispatches this {@link Action} to the store
   */
  public dispatch(): void {
    // NOTE: NgRx uses this weird marker type to detect errors which causes
    // problem when using deferred type parameters like we are here
    this.getFeature().store.dispatch(this satisfies Action.Constraint as Action)
  }

  /**
   * Produces an {@link rxjs.filter} that filters for another {@link Action}
   */
  public relatedBy<TPreFilter>({
    feature,
    op,
    origin,
    phase,
    request,
    trace,
  }: Action.relatedBy.Params) {
    return rxjs.filter<TPreFilter>(
      other =>
        other instanceof Action &&
        (!feature || other.featureId === this.featureId) &&
        (!origin || other.origin === this.origin) &&
        (!phase || other.phase === this.phase) &&
        (!request || other.requestId === this.requestId) &&
        (!trace || other.traceId === this.traceId) &&
        (typeof op === 'boolean'
          ? !op ||
            ((other.op satisfies Op as Op).name === this.op.name &&
              (other.op satisfies Op as Op).status === this.op.status)
          : (!op?.name ||
              (other.op satisfies Op as Op).name === this.op.name) &&
            (!op?.status ||
              (other.op satisfies Op as Op).status === this.op.status)),
    )
  }
}
export namespace Action {
  export const isAction: TypeChecker<Action> =
    TypeChecker.Factory.instance(Action)
  export const assertIsAction: TypeAsserter<Action> =
    TypeAsserter.Factory.from(isAction)

  export type Constraint<
    TState = any,
    TOp extends Op = any,
    TData = any,
  > = Action<TState, TOp, TData>

  export namespace constructor {
    export interface Params<TState, TOp extends Op, TData> {
      /**
       * @deprecated Use {@link reducer}
       */
      // TODO: remove this when we no longer need it for backwards compatability
      reducerDeprecated?: (
        state: TState,
        data: TData,
        globalState: object,
      ) => Readonly<NoInfer<TState>>
      reducer?: (params: {
        readonly state: TState
        readonly data: TData
        readonly action: Action<TState, TOp, TData>
        readonly globalState: object
      }) => Readonly<NoInfer<TState>>
      op: TOp
      data: TData
      feature: Feature.Constraint
      source: Action | string
      traceStash?: Record<string, unknown>
    }
  }

  export namespace relatedBy {
    export interface Params {
      readonly feature?: boolean
      readonly op?: boolean | Partial<Replace.Properties.Entirely<Op, boolean>>
      readonly origin?: boolean
      readonly phase?: boolean
      readonly request?: boolean
      readonly trace?: boolean
    }
  }
}
